import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, BarChart2, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'
import FunctionGraph from '../components/FunctionGraph'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'youtuber',
    emoji: '🎬',
    title: '유튜버',
    category: '크리에이터',
    color: 'from-red-400 to-rose-500',
    bg: 'bg-red-50',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-700',
    story: `중학교 2학년 지민이는 게임 유튜버가 꿈입니다.
채널을 개설한 지 얼마 안 됐지만, 매주 꾸준히 영상을 올리고 있어요.
지민이 채널의 첫날 구독자는 500명이었고, 매주 1,200명씩 늘고 있습니다.
"구독자가 언제 1만 명이 될까?" — 지민이는 궁금해졌어요.`,
    situation: '유튜브 채널 구독자: 처음 500명에서 시작해 매주 1,200명씩 증가합니다.',
    question: '6주 후 구독자 수는? 그리고 1만 명이 되려면 몇 주가 걸릴까요?',
    xLabel: '주 수',
    yLabel: '구독자 수 (명)',
    slope: 1200,
    intercept: 500,
    slopeDesc: '주당 +1,200명 (성장 속도)',
    interceptDesc: '초기 구독자 500명 (y절편)',
    equation: 'y = 1200x + 500',
    answer: '6주 후 → y = 1200 × 6 + 500 = 7,700명\n1만 명 달성 → 10000 = 1200x + 500 → x ≈ 7.9주 (약 8주)',
    whyLinear: '매주 같은 수의 구독자가 늘어나면 → 일정한 변화율 → 1차 함수!',
    realAnswer: 7700,
    unit: '명',
    tableData: [
      { x: 0, y: 500 }, { x: 2, y: 2900 }, { x: 4, y: 5300 },
      { x: 6, y: 7700 }, { x: 8, y: 10100 }, { x: 10, y: 12500 },
    ],
    quiz: [
      { q: '8주 후 구독자는 몇 명인가요?', a: '10,100명', hint: 'y = 1200×8 + 500' },
      { q: '구독자 1만 명 달성에 몇 주가 걸리나요?', a: '약 8주', hint: '10000 = 1200x + 500 → x ≈ 7.9' },
    ],
    insight: '유튜브 알고리즘 팀은 채널 성장 곡선을 분석해 추천 영상을 결정합니다. 바로 이 1차 함수가 기초!',
    jobs: ['데이터 분석가', '콘텐츠 마케터', '유튜브 알고리즘 개발자'],
  },
  {
    id: 'soccer',
    emoji: '⚽',
    title: '축구선수',
    category: '스포츠',
    color: 'from-green-400 to-emerald-500',
    bg: 'bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-100 text-green-700',
    story: `손흥민을 꿈꾸는 준서는 매일 학교 운동장에서 슈팅 연습을 합니다.
처음엔 10번 차면 3골을 넣었어요 (성공률 30%).
그런데 매주 연습할수록 성공 횟수가 늘어났습니다.
처음 3골에서 시작해, 매주 2골씩 더 넣을 수 있게 됐어요.
"이대로라면 언제쯤 10골을 넣을 수 있을까?"`,
    situation: '슈팅 성공 횟수: 처음 3골에서 시작해 매주 2골씩 증가합니다.',
    question: '5주 후 성공 횟수는? 그리고 10골을 달성하려면 몇 주가 걸릴까요?',
    xLabel: '연습 주 수',
    yLabel: '슈팅 성공 수 (골)',
    slope: 2,
    intercept: 3,
    slopeDesc: '주당 +2골 (실력 향상 속도)',
    interceptDesc: '초기 성공 3골 (y절편)',
    equation: 'y = 2x + 3',
    answer: '5주 후 → y = 2 × 5 + 3 = 13골\n10골 달성 → 10 = 2x + 3 → x = 3.5주 (약 4주)',
    whyLinear: '꾸준한 연습으로 매주 같은 만큼 실력이 향상된다면 → 1차 함수!',
    realAnswer: 13,
    unit: '골',
    tableData: [
      { x: 0, y: 3 }, { x: 2, y: 7 }, { x: 4, y: 11 },
      { x: 5, y: 13 }, { x: 7, y: 17 }, { x: 10, y: 23 },
    ],
    quiz: [
      { q: '10주 후 슈팅 성공 횟수는?', a: '23골', hint: 'y = 2×10 + 3' },
      { q: '20골을 넣으려면 몇 주 연습해야 하나요?', a: '8.5주 (약 9주)', hint: '20 = 2x + 3 → x = 8.5' },
    ],
    insight: '프로 스카우터들은 선수의 성장 곡선을 분석해 영입을 결정합니다. 성장률이 바로 기울기!',
    jobs: ['스포츠 데이터 분석가', '피지컬 코치', 'AI 스카우팅 개발자'],
  },
  {
    id: 'chef',
    emoji: '👨‍🍳',
    title: '셰프',
    category: '요리',
    color: 'from-orange-400 to-amber-400',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    story: `서울 유명 레스토랑의 셰프 지망생 수아는
오늘 파스타를 만들기 위해 재료비를 계산하고 있습니다.
파스타 1인분의 재료비는 2,500원, 여기에 기본 준비 비용(주방 세팅)이 10,000원 들어갑니다.
손님이 많이 올수록 총 비용이 얼마나 될까요?
그리고 파스타 1인분에 8,000원을 받으면, 몇 명부터 이익이 날까요?`,
    situation: '파스타 재료비: 기본 준비 비용 10,000원 + 1인분당 2,500원.',
    question: '20인분을 만들면 총 재료비는? 그리고 판매가 8,000원/인분일 때 몇 명부터 이익?',
    xLabel: '인분 수',
    yLabel: '비용 또는 수익 (원)',
    slope: 2500,
    intercept: 10000,
    slopeDesc: '1인분당 +2,500원 (변동 비용)',
    interceptDesc: '기본 준비 비용 10,000원 (고정 비용)',
    equation: '총비용: y = 2500x + 10000\n총수익: y = 8000x',
    answer: '20인분 총비용 → y = 2500 × 20 + 10000 = 60,000원\n손익분기점 → 8000x = 2500x + 10000 → x ≈ 1.8명 (2명부터 이익!)',
    whyLinear: '인분이 늘수록 재료비가 일정하게 증가 → 1차 함수! 두 직선의 교점이 손익분기점!',
    realAnswer: 60000,
    unit: '원',
    tableData: [
      { x: 0, y: 10000 }, { x: 5, y: 22500 }, { x: 10, y: 35000 },
      { x: 15, y: 47500 }, { x: 20, y: 60000 }, { x: 30, y: 85000 },
    ],
    quiz: [
      { q: '30인분 총 재료비는 얼마인가요?', a: '85,000원', hint: 'y = 2500×30 + 10000' },
      { q: '하루 파스타 수익이 100,000원이 되려면 몇 인분 팔아야 하나요?', a: '13인분', hint: '8000x - (2500x + 10000) = 100000 → x ≈ 20인분... 재료비 제외 순이익 기준으로 계산' },
    ],
    insight: '유명 셰프들은 레시피마다 원가를 계산해 메뉴 가격을 정합니다. 이것이 바로 1차 함수의 실제 활용!',
    jobs: ['식음료 원가 분석가', '레스토랑 경영 컨설턴트', '푸드테크 스타트업 창업자'],
  },
  {
    id: 'doctor',
    emoji: '👨‍⚕️',
    title: '의사',
    category: '의료',
    color: 'from-blue-400 to-cyan-400',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    story: `소아과 의사 박 선생님은 아이들의 성장을 관찰합니다.
태어났을 때 50cm였던 아기가 매달 2.5cm씩 자라고 있어요.
"이 아이의 키가 정상 범위인지 판단하려면 몇 개월 후의 키를 예측해야 해."
의사들은 성장 곡선(1차 함수)을 사용해 아이의 발육 상태를 확인합니다.`,
    situation: '아기 키: 태어날 때 50cm에서 시작해 매달 2.5cm씩 성장합니다.',
    question: '12개월(1살) 후 키는? 키가 75cm가 되는 시점은?',
    xLabel: '개월 수',
    yLabel: '키 (cm)',
    slope: 2.5,
    intercept: 50,
    slopeDesc: '월 +2.5cm (성장 속도)',
    interceptDesc: '출생 시 키 50cm (y절편)',
    equation: 'y = 2.5x + 50',
    answer: '12개월 후 → y = 2.5 × 12 + 50 = 80cm\n75cm 달성 → 75 = 2.5x + 50 → x = 10개월',
    whyLinear: '일정한 속도로 자라는 초기 성장 구간은 1차 함수로 근사할 수 있습니다!',
    realAnswer: 80,
    unit: 'cm',
    tableData: [
      { x: 0, y: 50 }, { x: 3, y: 57.5 }, { x: 6, y: 65 },
      { x: 9, y: 72.5 }, { x: 12, y: 80 }, { x: 18, y: 95 },
    ],
    quiz: [
      { q: '6개월 후 아기의 키는?', a: '65cm', hint: 'y = 2.5×6 + 50' },
      { q: '키가 90cm가 되려면 몇 개월이 걸리나요?', a: '16개월', hint: '90 = 2.5x + 50 → x = 16' },
    ],
    insight: '의사들은 성장 곡선 그래프를 보고 아이의 발육이 정상인지 판단합니다. 이 그래프가 바로 1차 함수!',
    jobs: ['소아과 의사', '성장클리닉 전문의', '의료 AI 개발자', '헬스케어 데이터 과학자'],
  },
  {
    id: 'idol',
    emoji: '🎤',
    title: '아이돌 가수',
    category: '연예',
    color: 'from-purple-400 to-pink-400',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-700',
    story: `K-POP 아이돌을 꿈꾸는 예원이는 매일 연습실에서 5시간씩 연습합니다.
처음 입소했을 때 댄스 점수(100점 만점)는 45점이었어요.
엄격한 선생님이 말했습니다: "하루 1시간 연습할 때마다 점수가 1.5점씩 오른단다."
예원이는 생각했어요: "데뷔 기준 점수 80점을 받으려면 총 몇 시간을 연습해야 하지?"`,
    situation: '댄스 점수: 처음 45점에서 시작해 연습 1시간마다 1.5점씩 향상됩니다.',
    question: '30시간 연습 후 점수는? 그리고 데뷔 기준 80점을 달성하려면 몇 시간이 필요할까요?',
    xLabel: '연습 시간 (시간)',
    yLabel: '댄스 점수 (점)',
    slope: 1.5,
    intercept: 45,
    slopeDesc: '시간당 +1.5점 (실력 향상률)',
    interceptDesc: '초기 점수 45점 (y절편)',
    equation: 'y = 1.5x + 45',
    answer: '30시간 후 → y = 1.5 × 30 + 45 = 90점\n80점 달성 → 80 = 1.5x + 45 → x ≈ 23.3시간 (약 24시간)',
    whyLinear: '꾸준히 같은 강도로 연습하면 실력이 일정하게 오름 → 1차 함수!',
    realAnswer: 90,
    unit: '점',
    tableData: [
      { x: 0, y: 45 }, { x: 10, y: 60 }, { x: 20, y: 75 },
      { x: 24, y: 81 }, { x: 30, y: 90 }, { x: 37, y: 100.5 },
    ],
    quiz: [
      { q: '20시간 연습 후 점수는?', a: '75점', hint: 'y = 1.5×20 + 45' },
      { q: '만점(100점)을 받으려면 몇 시간이 필요한가요?', a: '약 37시간', hint: '100 = 1.5x + 45 → x ≈ 36.7' },
    ],
    insight: '엔터테인먼트 회사들은 연습생의 성장 데이터를 분석해 데뷔 시점을 결정합니다. 바로 1차 함수의 활용!',
    jobs: ['연예 기획자', '퍼포먼스 코치', '음악 스트리밍 데이터 분석가'],
  },
  {
    id: 'detective',
    emoji: '🕵️',
    title: '탐정',
    category: '추리',
    color: 'from-slate-500 to-gray-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    badge: 'bg-slate-100 text-slate-700',
    story: `사립 탐정 김 씨는 의뢰를 받으면 하루 50,000원의 기본료와
조사 1시간당 15,000원의 추가 비용을 청구합니다.
어느 날 한 의뢰인이 물었습니다:
"선생님, 제 사건 해결에 총 8시간이 걸렸다면 비용이 얼마인가요?"
탐정이 답했습니다: "계산해 드리죠. 1차 함수만 알면 바로 나옵니다!"`,
    situation: '탐정 비용: 기본료 50,000원 + 조사 1시간당 15,000원 추가.',
    question: '8시간 조사 시 총 비용은? 그리고 예산이 200,000원이라면 최대 몇 시간 조사 가능할까요?',
    xLabel: '조사 시간 (시간)',
    yLabel: '총 비용 (원)',
    slope: 15000,
    intercept: 50000,
    slopeDesc: '시간당 +15,000원 (추가 비용)',
    interceptDesc: '기본료 50,000원 (y절편)',
    equation: 'y = 15000x + 50000',
    answer: '8시간 → y = 15000 × 8 + 50000 = 170,000원\n200,000원 예산 → 200000 = 15000x + 50000 → x = 10시간',
    whyLinear: '기본료(고정값)에 시간당 일정 비용이 추가 → 완벽한 1차 함수!',
    realAnswer: 170000,
    unit: '원',
    tableData: [
      { x: 0, y: 50000 }, { x: 2, y: 80000 }, { x: 4, y: 110000 },
      { x: 6, y: 140000 }, { x: 8, y: 170000 }, { x: 10, y: 200000 },
    ],
    quiz: [
      { q: '6시간 조사 비용은 얼마인가요?', a: '140,000원', hint: 'y = 15000×6 + 50000' },
      { q: '예산 250,000원으로 최대 몇 시간 조사 가능한가요?', a: '13시간', hint: '250000 = 15000x + 50000 → x = 13.3 → 13시간' },
    ],
    insight: '변호사, 컨설턴트, 프리랜서 등 시간제 요금을 받는 모든 직업에서 이 공식이 사용됩니다!',
    jobs: ['법률 보조인', '컨설턴트', '프리랜서 개발자', '경영 분석가'],
  },
  {
    id: 'programmer',
    emoji: '💻',
    title: '프로그래머',
    category: 'IT',
    color: 'from-indigo-400 to-blue-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    badge: 'bg-indigo-100 text-indigo-700',
    story: `신입 개발자 민준이는 첫 프로젝트를 맡았습니다.
앱을 처음 출시했을 때 사용자는 2,000명이었고,
마케팅 덕분에 매일 300명씩 새 사용자가 가입하고 있어요.
회사 목표는 출시 후 1개월(30일) 안에 1만 명을 달성하는 것!
민준이는 계산해봤습니다: "가능할까?"`,
    situation: '앱 사용자 수: 출시 시 2,000명에서 시작해 매일 300명씩 증가합니다.',
    question: '30일 후 사용자 수는? 그리고 1만 명 달성까지 몇 일이 걸릴까요?',
    xLabel: '일 수',
    yLabel: '사용자 수 (명)',
    slope: 300,
    intercept: 2000,
    slopeDesc: '일당 +300명 (가입 속도)',
    interceptDesc: '출시 시 사용자 2,000명 (y절편)',
    equation: 'y = 300x + 2000',
    answer: '30일 후 → y = 300 × 30 + 2000 = 11,000명 ✅ 목표 달성!\n1만 명 달성 → 10000 = 300x + 2000 → x ≈ 26.7일 (27일째)',
    whyLinear: '매일 같은 수의 사용자가 가입한다면 → 일정한 변화율 → 1차 함수!',
    realAnswer: 11000,
    unit: '명',
    tableData: [
      { x: 0, y: 2000 }, { x: 7, y: 4100 }, { x: 14, y: 6200 },
      { x: 21, y: 8300 }, { x: 27, y: 10100 }, { x: 30, y: 11000 },
    ],
    quiz: [
      { q: '14일 후 사용자 수는?', a: '6,200명', hint: 'y = 300×14 + 2000' },
      { q: '2만 명 달성까지 며칠이 걸리나요?', a: '약 60일', hint: '20000 = 300x + 2000 → x = 60' },
    ],
    insight: '앱 개발자들은 사용자 증가 추세를 1차 함수로 모델링해 서버 용량을 미리 준비합니다!',
    jobs: ['앱 개발자', '성장해킹 마케터', '데이터 엔지니어', 'CTO'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '👨‍💻',
    title: '소프트웨어 엔지니어',
    example: '가격 산정 알고리즘, 배송비 계산 시스템',
    formula: 'price = cost_per_unit × qty + base_fee',
    salary: '연 5,000만~1억원',
    color: 'from-blue-500 to-indigo-500',
  },
  {
    icon: '📊',
    title: '데이터 분석가',
    example: '매출 예측, 비용 추세 분석',
    formula: 'revenue = growth_rate × months + base',
    salary: '연 4,000만~8,000만원',
    color: 'from-purple-500 to-pink-500',
  },
  {
    icon: '🏗️',
    title: '건축·토목 엔지니어',
    example: '건물 층수에 따른 공사비 계산',
    formula: 'cost = price_per_floor × floors + foundation',
    salary: '연 4,500만~9,000만원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '⚕️',
    title: '의사·의료 연구자',
    example: '약 용량-반응 관계, 성장 곡선 예측',
    formula: 'dose = rate × weight + base_dose',
    salary: '연 8,000만~2억원+',
    color: 'from-red-500 to-rose-500',
  },
  {
    icon: '🌍',
    title: '환경·에너지 과학자',
    example: '탄소 배출량 예측, 에너지 소비 분석',
    formula: 'emissions = factor × usage + fixed',
    salary: '연 4,000만~7,000만원',
    color: 'from-green-500 to-teal-500',
  },
  {
    icon: '✈️',
    title: '항공우주 엔지니어',
    example: '연료 소모 예측, 비행 시간 계산',
    formula: 'fuel = burn_rate × time + reserve',
    salary: '연 5,500만~1억 2,000만원',
    color: 'from-sky-500 to-cyan-500',
  },
]

// ── 실생활 퀴즈 챌린지 ────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🛒',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '마트에서 사과 1개에 800원입니다. 사과 n개를 살 때 총 금액 y를 식으로 나타내면?',
    hint: 'y = 기울기 × x + y절편 (y절편은 0입니다)',
    answer: 'y = 800x',
    explanation: '기울기 = 800(개당 가격), y절편 = 0(0개 사면 0원). 이건 원점을 지나는 특수한 1차 함수(정비례)입니다!',
  },
  {
    id: 'c2',
    emoji: '🏠',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '월세 40만원짜리 집에 살면서 매월 30만원씩 저축합니다. x개월 후 총 저축액 y를 식으로 나타내면? (초기 저금 10만원)',
    hint: '월세는 저축과 관련 없습니다. 초기 저금 + 매월 저축을 생각해보세요',
    answer: 'y = 30x + 10 (만원)',
    explanation: '기울기 = 30(월 저축액), y절편 = 10(초기 저금). 월세는 함수에 영향 없음!',
  },
  {
    id: 'c3',
    emoji: '🚴',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '자전거 대여소 A는 기본요금 2,000원 + 분당 50원, B는 기본요금 없이 분당 100원. 어느 시점에서 A가 더 저렴해지나요?',
    hint: 'A: y = 50x + 2000, B: y = 100x. 두 식을 같다고 놓고 풀면?',
    answer: '40분 이상 이용 시 A가 더 저렴합니다',
    explanation: '50x + 2000 = 100x → 2000 = 50x → x = 40. 40분 미만은 B, 40분 이상은 A!',
  },
  {
    id: 'c4',
    emoji: '🌊',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '수조에 물이 200L 있습니다. 물을 빼면 분당 5L씩 줄어듭니다. 수조가 완전히 비는 데 몇 분 걸리나요?',
    hint: 'y = -5x + 200 에서 y = 0이 되는 x값은?',
    answer: '40분',
    explanation: '0 = -5x + 200 → 5x = 200 → x = 40. 음의 기울기를 가진 1차 함수!',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  교통: 'bg-orange-100 text-orange-700',
  금융: 'bg-pink-100 text-pink-700',
  과학: 'bg-blue-100 text-blue-700',
  일상: 'bg-purple-100 text-purple-700',
  크리에이터: 'bg-red-100 text-red-700',
  스포츠: 'bg-green-100 text-green-700',
  요리: 'bg-orange-100 text-orange-700',
  의료: 'bg-blue-100 text-blue-700',
  연예: 'bg-purple-100 text-purple-700',
  추리: 'bg-slate-100 text-slate-700',
  IT: 'bg-indigo-100 text-indigo-700',
}

// ── 계산기 컴포넌트 (강화판) ──────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: 'forward' | 'inverse'
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }

  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v)) return

    if (mode === 'forward') {
      // x → y  :  y = slope·x + intercept
      const raw = scenario.slope * v + scenario.intercept
      const y = Math.round(raw * 100) / 100
      setResult({
        mode: 'forward',
        inputVal: v,
        outputVal: y,
        steps: [
          `공식: ${scenario.equation}`,
          `x = ${v} 대입`,
          `y = ${scenario.slope} × ${v} + ${scenario.intercept}`,
          `y = ${scenario.slope * v} + ${scenario.intercept}`,
          `y = ${y.toLocaleString()} ${scenario.unit}`,
        ],
      })
    } else {
      // y → x  :  x = (y - intercept) / slope
      if (scenario.slope === 0) return
      const raw = (v - scenario.intercept) / scenario.slope
      const x = Math.round(raw * 100) / 100
      setResult({
        mode: 'inverse',
        inputVal: v,
        outputVal: x,
        steps: [
          `공식: ${scenario.equation}`,
          `${scenario.yLabel.split(' ')[0]} = ${v} 대입`,
          `${v} = ${scenario.slope} × x + ${scenario.intercept}`,
          `${scenario.slope} × x = ${v} − ${scenario.intercept} = ${v - scenario.intercept}`,
          `x = ${v - scenario.intercept} ÷ ${scenario.slope} = ${x}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.01 + 0.5
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split(' ')[0]
  const yLabelShort = scenario.yLabel.split(' ')[0]

  return (
    <div className="card border-2 border-primary-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-primary-600" />
          직접 계산해보기
        </p>
        {/* Mode toggle */}
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-primary-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            {yLabelShort} → {xLabelShort}
          </button>
        </div>
      </div>

      {/* Equation display */}
      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">함수식</span>
        <span className="font-mono font-bold text-primary-700 text-lg">{scenario.equation}</span>
      </div>

      {/* Input area */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🔢 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-primary-600 hover:bg-primary-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-purple-600 hover:bg-purple-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-3">
          {/* Step-by-step working */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1
                      ? 'bg-emerald-500 text-white'
                      : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1
                      ? 'font-black text-emerald-700 text-base'
                      : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Big result box */}
          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward'
              ? 'bg-primary-50 border-primary-300'
              : 'bg-purple-50 border-purple-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-primary-700' : 'text-purple-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : scenario.xLabel.split(' ')[1]?.replace(/[()]/g,'') || ''}
              </span>
            </div>
          </div>

          {/* Self-check: ask student to verify */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기 — 직접 계산한 후 입력해보세요!</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉 완벽합니다!</>
                  : <><XCircle size={16} /> 아쉬워요. 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>

          {/* Reset */}
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-emerald-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 퀴즈 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-emerald-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-emerald-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 1차 함수', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 1차 함수</h1>
            <p className="text-sm text-slate-500">유튜버, 축구선수, 쉐프, 의사, 아이돌, 탐정, 프로그래머 — 모두 1차 함수를 씁니다!</p>
          </div>
        </div>

        {/* Career Story Banner */}
        <div className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 1차 함수를 만나다!</p>
          <p className="text-sm opacity-90 mb-3">수학이 왜 필요한지 모르겠다고요? 당신이 꿈꾸는 직업이 바로 답입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
            {[
              { icon: '🎬', job: '유튜버' },
              { icon: '⚽', job: '축구선수' },
              { icon: '👨‍🍳', job: '셰프' },
              { icon: '👨‍⚕️', job: '의사' },
              { icon: '🎤', job: '아이돌' },
              { icon: '🕵️', job: '탐정' },
              { icon: '💻', job: '프로그래머' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          {/* Category Filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Scenario Cards */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-primary-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-2 space-y-4">
              {/* Situation Card */}
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                {/* Story */}
                {'story' in selected && (
                  <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 1차 함수</p>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                      {(selected as typeof SCENARIOS[0]).story}
                    </p>
                  </div>
                )}

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                {/* Equation Breakdown */}
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 함수식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-black text-primary-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">기울기 (a = {selected.slope})</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">y절편 (b = {selected.intercept})</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Question & Answer */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-emerald-700">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-amber-700">
                    <span className="font-semibold">왜 1차 함수인가?</span> {selected.whyLinear}
                  </p>
                </div>
              </div>

              {/* Graph */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <BarChart2 size={15} className="text-primary-500" />
                    그래프로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '그래프 보기' : '표로 보기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>

                {showTable ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">
                            {selected.xLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-primary-50 text-primary-700 text-left">
                            {selected.yLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">
                            변화율 (Δy/Δx)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-primary-700">
                              {row.y.toLocaleString()}
                            </td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500">
                              {i === 0 ? '—' : (
                                <span className="font-mono text-emerald-600">
                                  {((row.y - selected.tableData[i-1].y) / (row.x - selected.tableData[i-1].x)).toFixed(1)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      변화율이 항상 <span className="font-bold text-emerald-600">{selected.slope}</span>으로 일정 → 1차 함수 증명!
                    </p>
                  </div>
                ) : (
                  <FunctionGraph
                    slope={selected.slope / (Math.abs(selected.slope) > 100 ? Math.abs(selected.slope) / 2 : 1)}
                    intercept={selected.intercept / (Math.abs(selected.slope) > 100 ? Math.abs(selected.slope) / 2 : 1)}
                    height={240}
                    showKeyPoints
                    color={selected.id === 'candle' ? '#ef4444' : '#2563eb'}
                    xRange={[-1, 12]}
                  />
                )}
              </div>

              {/* Calculator */}
              <ScenarioCalculator scenario={selected} />

              {/* Quiz */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              {/* Jobs related */}
              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-primary-500" />
                  이 상황에서 1차 함수를 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-primary-100 text-primary-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              {/* Insight */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['y = ax + b', '기울기 = 변화율', 'y절편 = 초기값', '예측·계획에 활용'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Summary */}
          <div className="mt-8 card bg-gradient-to-br from-primary-50 to-accent-50 border-primary-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 1차 함수 실생활 공통 패턴</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '📐', title: '기울기 = 변화율', desc: '시간당 요금, 속도, 증가/감소율 등 "얼마나 빠르게 변하는가"', color: 'text-orange-600' },
                { icon: '🎯', title: 'y절편 = 초기값', desc: '기본요금, 시작 저금액, 초기 온도 등 "처음 시작 값"', color: 'text-blue-600' },
                { icon: '🔮', title: '예측 가능', desc: 'x값을 넣으면 y값 계산 → 미래 상황을 수학으로 예측!', color: 'text-emerald-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 1차 함수를 사용하는 직업들</h2>
            <p className="text-sm text-indigo-100">
              1차 함수(y = ax + b)는 전 세계 수천만 직업인이 매일 활용하는 핵심 도구입니다.
              지금 배우는 것이 미래 직업의 기반이 됩니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-primary-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* AI & 미래 직업 섹션 */}
          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 1차 함수가 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'AI 모델도 선형 함수를 기반으로',
                  desc: '딥러닝의 가장 기본 연산은 y = Wx + b (가중치×입력 + 편향). 1차 함수가 AI의 뼈대!',
                  color: 'border-yellow-400',
                },
                {
                  title: '데이터 분석의 출발점',
                  desc: '선형 회귀(Linear Regression)는 데이터 과학의 가장 기초적이고 강력한 도구.',
                  color: 'border-blue-400',
                },
                {
                  title: '비즈니스 의사결정',
                  desc: '비용 구조, 수익 예측, 손익분기점 계산 모두 1차 함수로 시작합니다.',
                  color: 'border-emerald-400',
                },
                {
                  title: '공학 설계의 기초',
                  desc: '신호 처리, 제어 시스템, 로봇 경로 계획 등 공학 전 분야의 기본.',
                  color: 'border-purple-400',
                },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 학습 경로 */}
          <div className="card border border-primary-200 bg-primary-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-primary-600" /> 1차 함수에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '1차 함수', active: true },
                { step: '중2', topic: '연립방정식', active: false },
                { step: '중3', topic: '이차함수', active: false },
                { step: '고1', topic: '수열·지수', active: false },
                { step: '고2', topic: '미적분 기초', active: false },
                { step: '대학', topic: '선형대수·통계', active: false },
                { step: '직업', topic: 'AI·데이터사이언스', active: false },
              ].map((item, i) => (
                <React.Fragment key={item.step}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-primary-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-amber-100">
              실제 상황에서 1차 함수를 직접 만들고 풀어보세요.
              식을 세우는 것이 핵심입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          {/* 핵심 공식 정리 */}
          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-amber-500" /> 실생활 문제 풀기 비법
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: '① 변수 정하기',
                  desc: '"무엇이 변하는가?" → x와 y를 정한다',
                  example: '시간이 x, 거리가 y',
                  color: 'bg-blue-50 border-blue-200 text-blue-800',
                },
                {
                  step: '② 기울기 찾기',
                  desc: '"x가 1 늘 때 y가 얼마나 변하나?" → a값 결정',
                  example: '1시간에 80km → a = 80',
                  color: 'bg-orange-50 border-orange-200 text-orange-800',
                },
                {
                  step: '③ y절편 찾기',
                  desc: '"x가 0일 때 y는?" → b값 결정',
                  example: '출발 시 40km 지점 → b = 40',
                  color: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-primary-50 border border-primary-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-primary-700">y = ax + b</p>
              <p className="text-xs text-primary-500 mt-1">a(기울기) = 변화율, b(y절편) = 시작값 → 완성!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
